/**
 * 結算版面自適應排版(純函式,無 Phaser/DOM 依賴)。
 *
 * 背景:boss-victory 結算原本把所有列硬編在 `GAME.height/2` 上下固定偏移,
 * 又有三個各自為政的 y 累加器(banner / campaign / extra),內容一多就互相
 * 重疊、被切、糊成一團(boss 2026-06-14 截圖回報)。
 *
 * 本檔提供兩層 API:
 *   1) computeBossVictoryLayout(screenH) / isLayoutWithinBounds —— 既有 6 列
 *      契約(title/bossName/campaign/kills/xp/extra),保留給舊測試與簡易用途。
 *   2) computeResultSections(opts) —— **新的分區排版器**。依「實際動態內容量」
 *      (獎勵列數、是否升等、是否有對比區…)由上而下分配「不重疊」的區塊 y。
 *      result-scene.ts 改用這個,徹底解決「多解鎖 + 升等」同框時的重疊/溢出。
 *
 * 設計原則:單一向下游標(cursor),每個區塊量好高度才往下推,任何兩區塊
 * 不可能重疊;最後整體在安全區內垂直配置(內容少→置中偏上、內容多→壓縮間距)。
 */

/* ========================================================================== *
 * Part 1 — 既有 6 列契約(保留)
 * ========================================================================== */

/** boss-victory 結算的列(由上到下的儀式順序)。 */
export type ResultRowKey =
  | 'title'      // 勝利大標
  | 'bossName'   // boss 名
  | 'campaign'   // 過關星等 + 評星規則
  | 'kills'      // 消滅數
  | 'xp'         // 指揮官經驗
  | 'extra';     // 其餘堆疊區起點

/** 單列排版結果。 */
export interface ResultRowLayout {
  /** 列中心 y(配合 setOrigin(0.5))。 */
  y: number;
  /** 該列建議字級(px)。 */
  fontSize: number;
}

export type ResultLayout = Record<ResultRowKey, ResultRowLayout>;

interface RowSpec {
  key: ResultRowKey;
  baseFont: number;
  minFont: number;
  lineMult: number;
}

const ROW_SPECS: readonly RowSpec[] = [
  { key: 'title',    baseFont: 56, minFont: 30, lineMult: 1.35 },
  { key: 'bossName', baseFont: 30, minFont: 20, lineMult: 1.45 },
  { key: 'campaign', baseFont: 26, minFont: 18, lineMult: 2.6 },
  { key: 'kills',    baseFont: 24, minFont: 16, lineMult: 1.5 },
  { key: 'xp',       baseFont: 40, minFont: 26, lineMult: 1.6 },
  { key: 'extra',    baseFont: 18, minFont: 14, lineMult: 1.4 },
];

/**
 * 依可見畫面高排版 boss-victory 結算各列(舊 6 列契約)。
 * 仍保證:由上到下嚴格遞增、不重疊、不超界、字級有可讀下限。
 */
export function computeBossVictoryLayout(screenH: number): ResultLayout {
  const H = Number.isFinite(screenH) && screenH > 0 ? screenH : 600;
  const topMargin = Math.max(24, Math.round(H * 0.06));
  const bottomMargin = Math.max(80, Math.round(H * 0.20));
  const usable = Math.max(1, H - topMargin - bottomMargin);

  const totalBase = ROW_SPECS.reduce((s, r) => s + r.baseFont * r.lineMult, 0);
  const minScale = 0.5;
  const scale = Math.max(minScale, Math.min(1, usable / totalBase));

  const rows = ROW_SPECS.map((r) => {
    const fontSize = Math.max(r.minFont, Math.round(r.baseFont * scale));
    const lineH = fontSize * r.lineMult;
    return { key: r.key, fontSize, lineH };
  });

  const totalH = rows.reduce((s, r) => s + r.lineH, 0);
  const slack = Math.max(0, usable - totalH);
  let cursor = topMargin + slack / 2;

  const out = {} as ResultLayout;
  for (const r of rows) {
    out[r.key] = { y: Math.round(cursor + r.lineH / 2), fontSize: r.fontSize };
    cursor += r.lineH;
  }
  return out;
}

/**
 * 驗證輔助(供測試):回傳排版是否「無重疊且不超界」。
 */
export function isLayoutWithinBounds(layout: ResultLayout, screenH: number): boolean {
  const keys: ResultRowKey[] = ['title', 'bossName', 'campaign', 'kills', 'xp', 'extra'];
  const halfH = (k: ResultRowKey) => layout[k].fontSize * 0.6;
  for (const k of keys) {
    if (layout[k].y - halfH(k) < 0) return false;
    if (layout[k].y + halfH(k) > screenH) return false;
  }
  for (let i = 1; i < keys.length; i++) {
    const prev = keys[i - 1];
    const cur = keys[i];
    const prevBottom = layout[prev].y + halfH(prev);
    const curTop = layout[cur].y - halfH(cur);
    if (curTop < prevBottom) return false;
  }
  return true;
}

/* ========================================================================== *
 * Part 2 — 分區排版器(result-scene 新版主用)
 * ========================================================================== */

/** 結算分區鍵 —— 由上到下的視覺帶。 */
export type ResultSectionKey =
  | 'title'      // 勝利/通關大標
  | 'stars'      // 三顆星(中心)
  | 'starRule'   // 「剩餘血量 X% → 得 N★」說明列
  | 'rewards'    // 動態獎勵卡列表(0..N)
  | 'xpBar'      // 指揮官 XP 進度條
  | 'stats'      // 統計格(波次/消滅/得分)
  | 'cta';       // 主要行動鈕帶(回地圖/再戰)

/** 一個矩形帶:中心 y + 頂端 + 高度,供 add 系列定位。 */
export interface SectionBox {
  /** 帶中心 y。 */
  y: number;
  /** 帶頂端 y。 */
  top: number;
  /** 帶高度。 */
  height: number;
}

export type ResultSections = Record<ResultSectionKey, SectionBox>;

/** 計算輸入:畫面尺寸 + 動態內容量。 */
export interface ResultSectionsInput {
  /** 邏輯畫布高(預設 600)。 */
  screenH?: number;
  /** 獎勵列數(解鎖武器 / 主武器 / 圖鑑 / 配件 / 段位 / 升等 …,0..N)。 */
  rewardCount: number;
  /** 是否顯示星等帶(闖關過關才有)。 */
  hasStars: boolean;
  /** 是否顯示星等規則說明列。 */
  hasStarRule: boolean;
  /** 是否顯示統計帶。 */
  hasStats: boolean;
}

/** 各帶的設計高度。 */
const SECTION_BASE = {
  titleH: 64,
  // stars 帶涵蓋「三星 + 評星規則 + (boss 關)配件解鎖」整個子堆疊(_showCampaignClear
  // 內部由 y 往下排 stars / rule(+26) / accessory(+48)),故給足高度避免壓到獎勵區。
  starsH: 96,
  starRuleH: 26,
  rewardRowH: 38,     // 每張獎勵卡的高度(含間距)
  rewardPadH: 12,     // 獎勵區上下內距
  xpBarH: 60,
  statsH: 74,
  ctaH: 134,          // 雙鈕 + safe-area 預留
  gap: 14,            // 帶間間距
} as const;

/** 獎勵區可顯示的最多卡數(超過縮卡高,確保不溢出)。 */
export const REWARDS_MAX_VISIBLE = 6;

/**
 * 依實際內容量,由上而下分配各分區的不重疊矩形帶。
 *
 * 演算法:
 *   1. 只納入「會顯示」的帶(rewardCount=0 → rewards 高 0;hasStars=false → 跳過)。
 *   2. CTA 帶永遠錨在底部安全區(高 ctaH)。
 *   3. 內容區 = [topPad, ctaTop - gap];累加各帶理想高 + gap。
 *   4. 內容超量 → 等比壓縮(scale 下限 0.62)+ 獎勵卡數封頂 REWARDS_MAX_VISIBLE;
 *      內容不足 → 從頂端 padding 起排,剩餘空白偏放底部(重心偏上,由上讀下)。
 *
 * 回傳每個帶的 { y(中心), top, height }。
 */
export function computeResultSections(input: ResultSectionsInput): ResultSections {
  const screenH = Number.isFinite(input.screenH) && (input.screenH ?? 0) > 0 ? input.screenH! : 600;
  const rewardCount = Math.min(
    REWARDS_MAX_VISIBLE,
    Math.max(0, Math.floor(input.rewardCount || 0)),
  );

  const ctaH = SECTION_BASE.ctaH;
  const topPad = Math.max(14, Math.round(screenH * 0.025));
  const ctaTop = screenH - ctaH;
  const contentBottom = ctaTop - SECTION_BASE.gap;
  const usable = Math.max(1, contentBottom - topPad);

  type Band = { key: ResultSectionKey; h: number };
  const bands: Band[] = [];
  bands.push({ key: 'title', h: SECTION_BASE.titleH });
  if (input.hasStars) bands.push({ key: 'stars', h: SECTION_BASE.starsH });
  if (input.hasStarRule) bands.push({ key: 'starRule', h: SECTION_BASE.starRuleH });
  const rewardsH = rewardCount > 0
    ? SECTION_BASE.rewardPadH * 2 + rewardCount * SECTION_BASE.rewardRowH
    : 0;
  bands.push({ key: 'rewards', h: rewardsH });
  bands.push({ key: 'xpBar', h: SECTION_BASE.xpBarH });
  if (input.hasStats) bands.push({ key: 'stats', h: SECTION_BASE.statsH });

  const visibleBands = bands.filter((b) => b.h > 0);
  const gapTotal = Math.max(0, visibleBands.length - 1) * SECTION_BASE.gap;
  const wanted = visibleBands.reduce((s, b) => s + b.h, 0) + gapTotal;

  const scale = wanted <= usable ? 1 : Math.max(0.62, usable / wanted);
  const gap = SECTION_BASE.gap * scale;

  const scaledTotal = visibleBands.reduce((s, b) => s + b.h * scale, 0)
    + Math.max(0, visibleBands.length - 1) * gap;
  const slack = Math.max(0, usable - scaledTotal);
  let cursor = topPad + Math.min(slack * 0.35, 40);

  const out = {} as ResultSections;
  const ensure = (key: ResultSectionKey, top: number, height: number) => {
    out[key] = { top: Math.round(top), height: Math.round(height), y: Math.round(top + height / 2) };
  };

  // 先把「所有」section key 預設成貼著內容頂端的 0 高 box,確保即使某帶不顯示
  // (hasStars/hasStats=false),caller 取 sections.stats.y 等也不會是 undefined。
  const ALL_KEYS: ResultSectionKey[] = ['title', 'stars', 'starRule', 'rewards', 'xpBar', 'stats', 'cta'];
  for (const k of ALL_KEYS) ensure(k, cursor, 0);

  for (const b of bands) {
    if (b.h <= 0) {
      ensure(b.key, cursor, 0); // 未顯示:0 高,游標不前進
      continue;
    }
    const h = b.h * scale;
    ensure(b.key, cursor, h);
    cursor += h + gap;
  }
  ensure('cta', ctaTop, ctaH);

  return out;
}
